import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Map, List, fromJS } from 'immutable';
import { connect } from 'react-redux';
import { push } from 'connected-react18-router';
import { bindActionCreators } from 'redux';

import * as browseActionCreators from '../../actions/browse';
import { BrowseAutoComplete } from './BrowseAutoComplete';
import { BrowseCheckbox } from './BrowseCheckbox';
import { BrowseControlContent } from './BrowseControlContent';
import { Tag } from './Tag';
import Checkbox from '../helpers/Checkbox';

const BrowseCountryFilter = props => {
  const {
    filter,
    continents,
    info,
    onChange,
    onGetCountriesByContinent,
    allCountriesSuggestions,
    handleGetContinentSuggestion,
    handleGetAllCountriesSuggestion,
    type,
  } = props;

  const buttonRef = useRef(null);
  const focusOutRef = useRef(false);

  useEffect(() => {
    if (focusOutRef.current) {
      buttonRef.current.focus();
      focusOutRef.current = false;
    }
  });

  const handleCheckboxChange = useCallback(() => {
    const checked = info.get('checked');

    if (!checked) {
      handleGetContinentSuggestion(type, '');
      handleGetAllCountriesSuggestion();
    }

    onChange({
      filterName: 'country',
      filterData: {
        checked: !checked,
      },
    });
  }, [info, type, onChange, handleGetContinentSuggestion, handleGetAllCountriesSuggestion]);

  const handleGetNextAllCountriesSuggestion = useCallback(
    ({ value }) => {
      const suggestions = allCountriesSuggestions.filter(country =>
        country.text.toLowerCase().includes(value.toLowerCase()),
      );

      onChange({
        filterName: filter.get('name'),
        filterData: {
          suggestions,
        },
      });
    },
    [allCountriesSuggestions, filter, onChange],
  );

  const handleChange = useCallback(
    (filterName, filterData) => {
      if (filterData.selected) {
        let selectedList = info.get('selectedList');
        const selected = fromJS(filterData.selected);
        const field = selected.has('id') ? 'id' : 'value';

        if (selectedList.findIndex(item => item.get(field) === selected.get(field)) === -1) {
          selectedList = selectedList.push(selected.set('include', true));
          filterData.selectedList = selectedList;
        }
        focusOutRef.current = true;
      }
      onChange({
        filterName,
        filterData,
      });
    },
    [info, onChange],
  );

  const handleRemoveTag = useCallback(
    event => {
      event.stopPropagation();

      const { name } = event.target;
      const tagIndex = parseInt(name.replace('tag', ''), 10);
      let selectedList = info.get('selectedList');

      selectedList = selectedList.delete(tagIndex);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList,
        },
      });
    },
    [filter, info, onChange],
  );

  const handleChangeStatus = useCallback(
    index => {
      const selectedList = info.get('selectedList');

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList: selectedList.setIn([index, 'exclude'], !selectedList.getIn([index, 'exclude'])),
        },
      });
    },
    [filter, info, onChange],
  );

  const handleAddTag = useCallback(() => {
    if (!filter.get('enableDynamicAdd', false) || info.get('text', '') === '') return false;

    let selectedList = info.get('selectedList');
    const selected = fromJS({
      value: info.get('text'),
      name: info.get('text'),
    });

    if (selectedList.findIndex(item => item.get('value') === selected.get('value')) === -1) {
      selectedList = selectedList.push(selected);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          text: '',
          selectedList,
        },
      });
    }

    return undefined;
  }, [filter, info, onChange]);

  const tagListContentCountry = info
    .get('selectedList')
    .filter(tag => tag)
    .map((tag, index) => {
      const continent = continents.find(continentItem => continentItem.id === tag.get('continentId'));
      const value = (
        <>
          <span>{tag.get('text')}</span>{' '}
          {continent && (
            <>
              (<i className="font-size-10">{continent.name}</i>)
            </>
          )}
        </>
      );

      return (
        <Tag
          key={tag.has('id') ? `id_${tag.get('id')}` : `value_${tag.get('value')}`}
          exclude={tag.get('exclude')}
          index={index}
          onChangeStatus={handleChangeStatus}
          onRemoveTag={handleRemoveTag}
          value={value}
        />
      );
    });

  const continentCheckboxes =
    continents &&
    continents.map(continent => {
      const id = `continent_${continent.id}`;

      return (
        <Checkbox
          key={id}
          className="mt3"
          id={id}
          label={continent.name}
          name={id}
          onChange={event => onGetCountriesByContinent(continent.id, event)}
          value={continent.id}
        />
      );
    });

  const autocompleteCountry = (
    <>
      <div className="form-inline">
        <BrowseAutoComplete
          filter={filter}
          info={info}
          onChange={({ filterName, filterData }) => handleChange(filterName, filterData)}
          onGetNextSuggestion={handleGetNextAllCountriesSuggestion}
          onGetSuggestion={handleGetNextAllCountriesSuggestion}
        >
          <span ref={buttonRef} className="input-group-addon" onClick={handleAddTag} tabIndex="-1">
            <i aria-hidden="true" className="fa fa-plus" />
          </span>
        </BrowseAutoComplete>
      </div>
      {tagListContentCountry}
    </>
  );

  return (
    <BrowseCheckbox filter={filter} info={info} onChange={handleCheckboxChange}>
      <BrowseControlContent variant="add">
        <div className="mb10">{continentCheckboxes}</div>
        <div className="mb10">{autocompleteCountry}</div>
      </BrowseControlContent>
    </BrowseCheckbox>
  );
};

BrowseCountryFilter.propTypes = {
  continents: PropTypes.instanceOf(List).isRequired,
  filter: PropTypes.instanceOf(Map).isRequired,
  handleGetAllCountriesSuggestion: PropTypes.func.isRequired,
  handleGetContinentSuggestion: PropTypes.func.isRequired,
  info: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onGetCountriesByContinent: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(browseActionCreators, dispatch),
  push,
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(BrowseCountryFilter));
